import { useState, useRef } from "react";
import { Upload, X, CheckCircle2, Loader2, FileText, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const YES_NO = ["Yes", "No"];

// Position-specific screening fields. `type`: select | text | checkboxes.
const SPECIFIC_FIELDS = {
  "cdl-driver": [
    { key: "CDL Class", type: "select", options: ["Class A", "Class B"], required: true },
    { key: "CDL State", type: "text", placeholder: "WI" },
    { key: "CDL Expiration Date", type: "text", placeholder: "MM/YYYY" },
    { key: "Air-Brake Endorsement", type: "select", options: YES_NO },
    { key: "Manual-Transmission Experience", type: "select", options: YES_NO },
    { key: "Medical Card Status", type: "select", options: ["Valid", "Expired", "None yet"] },
    { key: "Years of Commercial Driving", type: "select", options: ["Less than 1", "1–2", "3–5", "5+"] },
    { key: "Equipment Experience", type: "checkboxes", options: ["Concrete truck", "Dump truck", "Trailer", "Heavy equipment"] },
    { key: "Accidents in Last 3 Years", type: "select", options: ["0", "1", "2", "3+"] },
    { key: "Moving Violations (3 yrs)", type: "select", options: ["0", "1", "2", "3+"] },
    { key: "Able to Help with Labor Duties", type: "select", options: YES_NO },
  ],
  "concrete-laborer": [
    { key: "Concrete Experience", type: "select", options: ["None", "Less than 1 year", "1–2 years", "3+ years"], required: true },
    { key: "Form-Setting Experience", type: "select", options: YES_NO },
    { key: "Flatwork Experience", type: "select", options: YES_NO },
    { key: "Stamped/Decorative Experience", type: "select", options: YES_NO },
    { key: "Finishing Experience", type: "select", options: YES_NO },
    { key: "Power-Tool Experience", type: "select", options: YES_NO },
    { key: "Can Lift 50–80 lbs Repeatedly", type: "select", options: YES_NO, required: true },
    { key: "Can Work Outdoors & Start Early", type: "select", options: YES_NO, required: true },
    { key: "Desired Hourly Rate", type: "text", placeholder: "$/hour" },
  ],
};

const initialShared = {
  first_name: "", last_name: "", phone: "", email: "", city: "", state: "WI",
  start_date: "", availability: "", experience_years: "", previous_employer: "",
  has_license: "", reliable_transport: "", work_authorized: "",
};

export default function JobApplicationForm({ slug, jobTitle }) {
  const specific = SPECIFIC_FIELDS[slug] || [];
  const [form, setForm] = useState(initialShared);
  const [answers, setAnswers] = useState({});
  const [resume, setResume] = useState(null); // {id, name}
  const [docs, setDocs] = useState([]); // [{id, name}]
  const [consent, setConsent] = useState(false);
  const [certify, setCertify] = useState(false);
  const [uploading, setUploading] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const resumeRef = useRef(null);
  const docRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const toggleCheckbox = (k, opt) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[k]) ? a[k] : [];
      return { ...a, [k]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(kind);
    try {
      const { data } = await api.post("/careers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return { id: data.id, name: data.name };
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setUploading("");
    }
  };

  const handleResume = async (files) => {
    if (!files?.[0]) return;
    const r = await uploadFile(files[0], "resume");
    if (r) setResume(r);
  };

  const handleDocs = async (files) => {
    for (const file of Array.from(files).slice(0, 4)) {
      const r = await uploadFile(file, "docs");
      if (r) setDocs((d) => [...d, r]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    if (!consent || !certify) {
      toast.error("Please confirm the consent and certification checkboxes.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/jobs/${slug}/apply`, {
        ...form,
        answers,
        resume_id: resume?.id || null,
        document_ids: docs.map((d) => d.id),
        consent_contact: consent,
        certify,
        source: "careers",
      });
      setDone(true);
      toast.success("Application submitted!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-card border border-border p-10 text-center" data-testid="application-success">
        <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-bold mb-2">Application Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thanks, {form.first_name}! We got your application for <strong>{jobTitle}</strong> and will review it right away.
          If it's a fit, we'll reach out by phone or email.
        </p>
      </div>
    );
  }

  const renderSpecific = (f) => {
    if (f.type === "text") {
      return (
        <Input
          value={answers[f.key] || ""}
          onChange={(e) => setAnswer(f.key, e.target.value)}
          placeholder={f.placeholder || ""}
          className="mt-1.5 rounded-sm"
          data-testid={`app-field-${f.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        />
      );
    }
    if (f.type === "checkboxes") {
      const cur = Array.isArray(answers[f.key]) ? answers[f.key] : [];
      return (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {f.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={cur.includes(opt)} onCheckedChange={() => toggleCheckbox(f.key, opt)} data-testid={`app-check-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    return (
      <Select value={answers[f.key] || ""} onValueChange={(v) => setAnswer(f.key, v)}>
        <SelectTrigger className="mt-1.5 rounded-sm" data-testid={`app-field-${f.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {f.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border p-6 sm:p-8 space-y-6" data-testid="application-form">
      <div>
        <h3 className="font-heading text-2xl font-bold">Apply for {jobTitle}</h3>
        <p className="text-sm text-muted-foreground mt-1">All applications are reviewed by our team. Fields marked * are required.</p>
      </div>

      {/* Shared */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="font-semibold">First Name *</Label><Input value={form.first_name} onChange={set("first_name")} className="mt-1.5 rounded-sm" data-testid="app-first-name" /></div>
        <div><Label className="font-semibold">Last Name *</Label><Input value={form.last_name} onChange={set("last_name")} className="mt-1.5 rounded-sm" data-testid="app-last-name" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="font-semibold">Phone *</Label><Input value={form.phone} onChange={set("phone")} placeholder="(920) 555-0123" className="mt-1.5 rounded-sm" data-testid="app-phone" /></div>
        <div><Label className="font-semibold">Email *</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5 rounded-sm" data-testid="app-email" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="font-semibold">City</Label><Input value={form.city} onChange={set("city")} placeholder="Watertown" className="mt-1.5 rounded-sm" data-testid="app-city" /></div>
        <div><Label className="font-semibold">State</Label><Input value={form.state} onChange={set("state")} className="mt-1.5 rounded-sm" data-testid="app-state" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Employment Availability</Label>
          <Select value={form.availability} onValueChange={set("availability")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="app-availability"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Full-time">Full-time</SelectItem>
              <SelectItem value="Seasonal">Seasonal</SelectItem>
              <SelectItem value="Part-time">Part-time</SelectItem>
              <SelectItem value="Either">Either / Flexible</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="font-semibold">Earliest Start Date</Label><Input value={form.start_date} onChange={set("start_date")} placeholder="e.g. Immediately, or MM/DD" className="mt-1.5 rounded-sm" data-testid="app-start-date" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Years of Construction Experience</Label>
          <Select value={form.experience_years} onValueChange={set("experience_years")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="app-experience"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {["None", "Less than 1", "1–2", "3–5", "5+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="font-semibold">Previous Employer</Label><Input value={form.previous_employer} onChange={set("previous_employer")} className="mt-1.5 rounded-sm" data-testid="app-prev-employer" /></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="font-semibold">Driver's License?</Label>
          <Select value={form.has_license} onValueChange={set("has_license")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="app-license"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YES_NO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-semibold">Reliable Transportation?</Label>
          <Select value={form.reliable_transport} onValueChange={set("reliable_transport")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="app-transport"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YES_NO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-semibold">Authorized to Work in US?</Label>
          <Select value={form.work_authorized} onValueChange={set("work_authorized")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="app-authorized"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YES_NO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Position-specific */}
      {specific.length > 0 && (
        <div className="pt-2 border-t border-border">
          <h4 className="font-heading font-bold text-lg mb-1">{jobTitle} Questions</h4>
          <p className="text-xs text-muted-foreground mb-4">A few role-specific questions to speed up the process.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {specific.map((f) => (
              <div key={f.key} className={f.type === "checkboxes" ? "sm:col-span-2" : ""}>
                <Label className="font-semibold">{f.key}{f.required ? " *" : ""}</Label>
                {renderSpecific(f)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resume */}
      <div className="pt-2 border-t border-border">
        <Label className="font-semibold">Résumé (optional)</Label>
        <div onClick={() => resumeRef.current?.click()} className="mt-1.5 border-2 border-dashed border-border bg-muted/40 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer rounded-sm p-5 text-center" data-testid="app-resume-zone">
          <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => handleResume(e.target.files)} data-testid="app-resume-input" />
          {uploading === "resume" ? <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /> : resume ? (
            <span className="flex items-center justify-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-primary" /> {resume.name}</span>
          ) : (
            <span className="text-sm text-muted-foreground"><Upload className="h-4 w-4 inline mr-1" /> Upload résumé (PDF or DOC)</span>
          )}
        </div>
      </div>

      {/* Secure documents (CDL / medical card / certs) */}
      <div>
        <Label className="font-semibold flex items-center gap-1.5">
          {slug === "cdl-driver" ? "Upload CDL & Medical Card (optional)" : "Certifications / Documents (optional)"}
          <Lock className="h-3.5 w-3.5 text-primary" />
        </Label>
        <p className="text-xs text-muted-foreground mt-1">Stored securely. Only our hiring team can view these — never shared publicly.</p>
        <div onClick={() => docRef.current?.click()} className="mt-1.5 border-2 border-dashed border-border bg-muted/40 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer rounded-sm p-5 text-center" data-testid="app-docs-zone">
          <input ref={docRef} type="file" accept=".pdf,.doc,.docx,image/*" multiple className="hidden" onChange={(e) => handleDocs(e.target.files)} data-testid="app-docs-input" />
          {uploading === "docs" ? <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /> : (
            <span className="text-sm text-muted-foreground"><Upload className="h-4 w-4 inline mr-1" /> Upload documents (up to 4)</span>
          )}
        </div>
        {docs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {docs.map((d, i) => (
              <span key={d.id} className="flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1.5 rounded-sm">
                <FileText className="h-3.5 w-3.5" /> {d.name}
                <button type="button" onClick={() => setDocs((x) => x.filter((_, idx) => idx !== i))}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Consent */}
      <div className="space-y-3 pt-2 border-t border-border">
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="app-consent" />
          <span>I consent to being contacted by CG Concrete LLC by phone, text, and email about my application.</span>
        </label>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={certify} onCheckedChange={setCertify} className="mt-0.5" data-testid="app-certify" />
          <span>I certify that the information provided is true and complete to the best of my knowledge.</span>
        </label>
      </div>

      <Button type="submit" disabled={submitting} className="w-full h-12 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base" data-testid="app-submit">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Application"}
      </Button>
    </form>
  );
}
